import React, { useEffect, useState } from 'react';
import { CircleDot, Loader2, ChevronDown, ChevronUp, Plus, Trash2, Link2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { competitorsAPI } from '../services/api';

const CompetitorTyreSources = ({ onAssigned }) => {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [sources, setSources] = useState([]);
  const [busy, setBusy] = useState('');

  const load = () => competitorsAPI.getTyres().then((d) => { setData(d); setSources(d.sources); }).catch(() => {});
  useEffect(() => { load(); }, []);
  if (!data) return null;

  const save = async () => {
    setBusy('save');
    try { const r = await competitorsAPI.setTyreSources(sources); setSources(r.sources); toast.success('Sources pneus enregistrées'); } catch (e) { toast.error(e.response?.data?.detail || 'Enregistrement impossible'); } finally { setBusy(''); }
  };
  const assign = async (overwrite) => {
    const n = overwrite ? data.tyres_total : data.tyres_without_urls;
    if (!window.confirm(`Attribuer les URLs concurrentes à ${n} pneu(s) ${overwrite ? '(remplace les URLs existantes)' : 'sans concurrent'} ?`)) return;
    setBusy('assign');
    try { const r = await competitorsAPI.assignTyres(overwrite); toast.success(`${r.updated} pneu(s) suivis via ${r.sources} source(s)`); await load(); onAssigned?.(); } catch (e) { toast.error(e.response?.data?.detail || 'Attribution impossible'); } finally { setBusy(''); }
  };
  const edit = (i, patch) => setSources((s) => s.map((x, k) => (k === i ? { ...x, ...patch } : x)));

  return (
    <div className="mt-3 rounded-lg border bg-white" data-testid="competitors-tyre-sources">
      <button type="button" onClick={() => setOpen((o) => !o)} className="w-full flex items-center justify-between px-4 py-2.5 text-sm" data-testid="competitors-tyres-toggle">
        <span className="inline-flex items-center gap-2 font-medium text-gray-800">
          <CircleDot size={15} className="text-orange-500" /> Veille pneus par dimension
          <span className="text-[11px] px-2 py-0.5 rounded-full bg-gray-100 text-gray-600" data-testid="competitors-tyres-status">{data.tyres_total} pneu(s) · {data.tyres_without_urls} sans concurrent</span>
        </span>
        {open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
      </button>
      {open && (
        <div className="px-4 pb-4 border-t pt-3 space-y-3 text-sm">
          <p className="text-xs text-gray-500">Pour chaque pneu, une URL de liste est générée chez chaque source à partir de sa dimension : l'IA relève ensuite le prix le plus bas de l'offre correspondant exactement à la marque et à la gamme. Variables : {data.placeholders.join(' ')}.</p>
          <div className="space-y-2">
            {sources.map((s, i) => (
              <div key={i} className="flex flex-wrap items-center gap-2" data-testid={`competitors-tyre-source-${i}`}>
                <input type="checkbox" checked={s.active !== false} onChange={(e) => edit(i, { active: e.target.checked })} data-testid={`competitors-tyre-source-active-${i}`} />
                <Input value={s.name} onChange={(e) => edit(i, { name: e.target.value })} className="h-8 w-44 text-xs" placeholder="Nom" data-testid={`competitors-tyre-source-name-${i}`} />
                <Input value={s.template} onChange={(e) => edit(i, { template: e.target.value })} className="h-8 flex-1 min-w-[280px] text-xs font-mono" placeholder="https://…?w={w}&h={h}&d={d}" data-testid={`competitors-tyre-source-template-${i}`} />
                <button type="button" onClick={() => setSources((x) => x.filter((_, k) => k !== i))} className="p-1 text-gray-400 hover:text-red-600" data-testid={`competitors-tyre-source-remove-${i}`}><Trash2 size={14} /></button>
              </div>
            ))}
            {sources.length < 6 && <button type="button" onClick={() => setSources((s) => [...s, { name: '', template: '', active: true }])} className="inline-flex items-center gap-1 text-xs text-[#1e3a5f] hover:underline" data-testid="competitors-tyre-source-add"><Plus size={12} /> Ajouter une source</button>}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button size="sm" onClick={save} disabled={!!busy} className="bg-[#1e3a5f] hover:bg-[#1e3a5f]/90" data-testid="competitors-tyres-save">{busy === 'save' && <Loader2 size={14} className="mr-1 animate-spin" />} Enregistrer les sources</Button>
            <Button size="sm" variant="outline" onClick={() => assign(false)} disabled={!!busy || !data.tyres_without_urls} data-testid="competitors-tyres-assign">{busy === 'assign' ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Link2 size={14} className="mr-1" />} Suivre les {data.tyres_without_urls} pneu(s) sans concurrent</Button>
            <Button size="sm" variant="ghost" onClick={() => assign(true)} disabled={!!busy || !data.tyres_total} data-testid="competitors-tyres-assign-all">Réattribuer à tous les pneus</Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CompetitorTyreSources;
